/**
 * A select that can be searched, and that can show more than a string —
 * card #19.
 *
 * Every picker in Slate was a native `<select>`. That is the right default
 * until a list gets long or an option needs more than a word: choosing an
 * assignee out of forty people meant scrolling a list with no way to type at
 * it, and a status could not carry its own colour. Both are hours-of-use
 * problems (§13, and Brad's brief), which is why this exists.
 *
 * What it deliberately keeps from the native control:
 *  - It commits **once**, on a deliberate pick. Arrow-keying a closed native
 *    `<select>` fires `change` per keypress, which is where `CommittedSelect`
 *    and its draft/flush dance came from; a combobox moves an *active*
 *    highlight instead and writes only on Enter or click, so that whole class
 *    of accidental audited writes goes away rather than being worked around.
 *  - It is one tab stop, and Escape gives up without changing anything.
 *
 * What it adds: type-to-filter over labels and keywords, an icon or colour
 * swatch per row, and a second line of detail where a row needs one.
 */
import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra text the filter matches — an email, a status category, a key. */
  keywords?: string[];
  /** A swatch, avatar or glyph shown before the label. */
  icon?: ReactNode;
  /** A muted second line: what this option means, when the label cannot say. */
  description?: string;
  /**
   * A heading this option sits under, replacing `<optgroup>`. Options are
   * rendered in the order given; a group header appears each time the group
   * changes, so callers group by ordering rather than by nesting.
   */
  group?: string;
  disabled?: boolean;
}

/**
 * Below this many options a search box is clutter, above it the list is a
 * scroll. Seven is where our own lists divide: priorities and categories sit
 * under it, people and statuses-with-history sit over it.
 */
const SEARCH_THRESHOLD = 7;

export function Combobox({
  id,
  label,
  value,
  options,
  onChange,
  placeholder = 'Select…',
  emptyText = 'No matches',
  hotkey,
  disabled,
  className,
  contentClassName,
  /** Keeps the trigger showing `placeholder` after a pick — for action menus. */
  resetAfterSelect = false,
  /** Opens on mount, for pickers revealed by an "Add" button. */
  defaultOpen = false,
  onOpenChange,
  accent,
}: {
  /** So a `<Label htmlFor>` still focuses it, as it did the native control. */
  id?: string;
  label: string;
  value: string | null;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  hotkey?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  resetAfterSelect?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * #81 — the selected value's colour, shown as a bar against the trigger's
   * left border instead of as an inline swatch before the label.
   *
   * An inline swatch is the right default in the option list, where rows are
   * being compared to each other. On the trigger it is wrong for a different
   * reason: the trigger sits in a column of other controls, and a 10px dot
   * plus its gap moved Status's label 16px right of every label under it. The
   * colour is worth keeping and the alignment is worth more, so the colour
   * moves out of the text flow rather than out of the control.
   *
   * Only the trigger changes. Options keep their `icon`.
   */
  accent?: string | null;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const selected = resetAfterSelect ? null : (options.find((o) => o.value === value) ?? null);
  const searchable = options.length >= SEARCH_THRESHOLD;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter((option) => {
      if (option.label.toLowerCase().includes(needle)) return true;
      if (option.description?.toLowerCase().includes(needle)) return true;
      return (option.keywords ?? []).some((word) => word.toLowerCase().includes(needle));
    });
  }, [options, query]);

  // Opening lands on the current value, so Enter twice is a no-op rather than
  // a silent change to whatever happened to be first.
  useEffect(() => {
    if (!open) return;
    const index = matches.findIndex((option) => option.value === value);
    setActive(index >= 0 ? index : 0);
    setQuery('');
    // Only when the popover opens: re-running this on every keystroke would
    // fight the arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Filtering can strand the highlight past the end of a shorter list.
  useEffect(() => {
    setActive((current) => (current >= matches.length ? 0 : current));
  }, [matches.length]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const change = (next: boolean): void => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const commit = (option: ComboboxOption | undefined): void => {
    if (!option || option.disabled) return;
    onChange(option.value);
    change(false);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // Wrapping, because a list you cannot fall off the end of is faster to
      // reach the last item of.
      setActive((current) => {
        if (matches.length === 0) return 0;
        return (current + step + matches.length) % matches.length;
      });
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(0, matches.length - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(matches[active]);
      return;
    }
    if (event.key === 'Tab') change(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={disabled ? undefined : change}>
      <Popover.Trigger asChild>
        <button
          type="button"
          role="combobox"
          {...(id ? { id } : {})}
          aria-label={label}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          disabled={disabled}
          {...(hotkey ? { 'data-hotkey': hotkey } : {})}
          className={cn(
            'border-input bg-background relative flex h-8 w-full items-center gap-1.5 rounded-md border px-2 text-sm',
            'focus-visible:ring-ring focus:outline-none focus-visible:ring-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            // §13.6 — a coarse pointer gets a target it can actually hit.
            '[@media(pointer:coarse)]:h-11',
            className,
          )}
        >
          {accent ? (
            <span
              aria-hidden
              className="absolute left-0 top-0 h-full w-[3px] rounded-l-md"
              style={{ backgroundColor: accent }}
            />
          ) : null}
          {selected?.icon && !accent ? <span className="shrink-0">{selected.icon}</span> : null}
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected?.label ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-auto size-3.5 shrink-0 opacity-50" aria-hidden />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onKeyDown={onKeyDown}
          className={cn(
            'bg-popover text-popover-foreground z-50 w-[var(--radix-popover-trigger-width)] min-w-48 overflow-hidden rounded-md border shadow-md',
            contentClassName,
          )}
        >
          {searchable ? (
            <div className="border-border flex items-center gap-1.5 border-b px-2">
              <Search className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
              <input
                autoFocus
                value={query}
                aria-label={`Search ${label}`}
                aria-controls={listId}
                placeholder="Type to filter…"
                className="placeholder:text-muted-foreground h-8 w-full bg-transparent text-sm focus:outline-none"
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          ) : null}

          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label}
            // Tall enough to be worth filtering, short enough to stay on
            // screen at 150% zoom, which is how Slate gets read.
            className="max-h-64 overflow-y-auto p-1"
            // The search box owns focus when there is one; without it the
            // content itself must take the keys.
            {...(searchable ? {} : { tabIndex: -1, autoFocus: true })}
          >
            {matches.length === 0 ? (
              <p className="text-muted-foreground px-2 py-3 text-center text-sm">{emptyText}</p>
            ) : (
              matches.map((option, index) => (
                <Fragment key={option.value}>
                  {option.group && option.group !== matches[index - 1]?.group ? (
                    <p className="text-muted-foreground px-2 pb-0.5 pt-1.5 text-xs font-medium">
                      {option.group}
                    </p>
                  ) : null}
                  <div
                    role="option"
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled}
                    data-active={index === active}
                    className={cn(
                      'flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                      'data-[active=true]:bg-accent data-[active=true]:text-accent-foreground',
                      option.disabled && 'pointer-events-none opacity-50',
                      '[@media(pointer:coarse)]:py-2.5',
                    )}
                    onPointerMove={() => setActive(index)}
                    onClick={() => commit(option)}
                  >
                    {option.icon ? <span className="shrink-0">{option.icon}</span> : null}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.description ? (
                        <span className="text-muted-foreground block truncate text-xs">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {option.value === value ? (
                      <Check className="size-3.5 shrink-0" aria-hidden />
                    ) : null}
                  </div>
                </Fragment>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** A status or tag's colour, as a row's icon. */
export function ColorDot({ color }: { color: string }): JSX.Element {
  return (
    <span
      aria-hidden
      className="size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}
